'use client';

import { twMerge } from 'tailwind-merge';
import { faArrowProgress } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { RawSVGImage } from '../RawSVGImage';
import { CommonImage } from '../CommonImage';

import { IComponentProcessStepsSection } from '@/types/cms';

type ProcessStepProps = NonNullable<IComponentProcessStepsSection['steps']>[number];

const ProcessStep = ({
  title,
  description,
  icon,
  classes,
  classes_title,
  classes_description,
  classes_icon,
}: ProcessStepProps) => {
  return (
    <div className={twMerge(styles.item, classes)}>
      <div className={styles.itemHeader}>
        {!icon && <FontAwesomeIcon icon={faArrowProgress} className={twMerge('size-8', classes_icon)} />}
        {!!icon && icon.startsWith('<svg') && (
          <RawSVGImage svg={icon} className={twMerge(styles.itemIcon, classes_icon)} />
        )}
        {!!icon && !icon.startsWith('<svg') && (
          <CommonImage src={icon} alt="" className={twMerge(styles.itemIcon, classes_icon)} />
        )}
      </div>
      <div className={styles.itemContent}>
        <div className={twMerge(styles.itemTitle, classes_title)}>{title}</div>
        <div className={twMerge(styles.itemDescription, classes_description)}>{description}</div>
      </div>
    </div>
  );
};

export const ProcessStepsSection = ({
  classes,
  classes_container,
  classes_description,
  classes_section,
  classes_title,
  description,
  steps,
  title,
}: IComponentProcessStepsSection) => {
  return (
    <div className={twMerge(styles.section, classes_section)}>
      <div className={twMerge(styles.container, classes_container)}>
        <div className={twMerge(styles.component, classes)}>
          <div className={styles.header}>
            <div className={twMerge(styles.title, classes_title)}>{title}</div>
            <div className={twMerge(styles.text, classes_description)}>{description}</div>
          </div>
          <div className={styles.content}>
            {steps.map((step, index) => (
              <ProcessStep {...step} key={index} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  section: 'bg-white font-helvetica text-blue-700',
  container: 'py-16 md:py-24 px-8 md:px-32 xl:px-4 xl:container mx-auto',
  component: 'flex flex-col gap-12',
  header: 'flex flex-col gap-2',
  title: 'text-[24px] md:text-[34px] xl:text-[40px] font-bold',
  text: 'text-[14px] md:text-[16px] xl:text-[18px]',
  content: 'flex flex-col items-stretch xl:flex-row gap-8 xl:gap-24',
  item: 'w-full flex flex-row items-start gap-4',
  itemHeader: 'p-4 bg-blue-100 rounded-md',
  itemIcon: 'min-w-[32px] min-h-[32px]',
  itemContent: 'flex flex-col gap-2 w-full',
  itemTitle: 'text-[20px] xl:text-[24px] font-bold',
  itemDescription: 'text-[14px] md:text-[16px] xl:text-[18px]',
};
